<template>
  <section class="psc" aria-labelledby="psc-h">
    <div class="psc-top">
      <div class="psc-titles">
        <div class="psc-eyebrow">Visibility over time</div>
        <div class="psc-titlerow">
          <h2 id="psc-h">Provider Visibility Score</h2>
          <button
            type="button"
            class="psc-info"
            :aria-expanded="infoOpen ? 'true' : 'false'"
            aria-label="How to read this score"
            @click="infoOpen = !infoOpen"
            @keydown.esc="infoOpen = false"
          >
            <v-icon size="20">mdi-information-outline</v-icon>
          </button>
          <div v-if="infoOpen" role="tooltip" class="psc-tip">{{ INFO }}</div>
        </div>
      </div>
      <div role="group" aria-label="Line type" class="psc-toggle">
        <button type="button" :aria-pressed="!avg7" :class="{ on: !avg7 }" @click="avg7 = false">Daily</button>
        <button type="button" :aria-pressed="avg7" :class="{ on: avg7 }" @click="avg7 = true">7-day avg</button>
      </div>
    </div>
    <div class="psc-legend">
      <span class="psc-line" aria-hidden="true" />
      <strong>{{ name }}</strong>
      <span>{{ todayText }}</span>
    </div>
    <highcharts v-if="daily.length" :options="options" class="psc-chart" />
    <div v-else class="psc-empty">No scores yet. They appear after the first nightly run.</div>
  </section>
</template>

<script>
import { Chart } from 'highcharts-vue'

const INFO =
  'The Provider Visibility Score takes into account how high a provider ranks in the queries. The score gives more weight to a provider that ranks #1 than a provider that ranks #5.'

// 'YYYY-MM-DD' as a plain day count, with no timezone shift.
function dayNumber(date) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '')
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 : null
}

export default {
  name: 'ProviderScoreChart',
  components: { highcharts: Chart },
  props: {
    daily: { type: Array, default: () => [] },
    today: { type: Number, default: null },
    providerName: { type: String, default: '' }
  },
  data() {
    return { avg7: false, infoOpen: false, INFO }
  },
  computed: {
    name() {
      return this.providerName || 'This provider'
    },
    todayText() {
      return this.today == null ? '—' : `${Math.round(this.today)}% today`
    },
    points() {
      const raw = this.daily.map(d => d.score)
      if (!this.avg7) return raw
      // Trailing mean over the point's date and the 6 calendar days before it.
      // The series skips days with no answers, so slice by date, not by position.
      const days = this.daily.map(d => dayNumber(d.date))
      return raw.map((_, i) => {
        if (days[i] == null) return null
        const s = raw.filter((v, j) => v != null && days[j] != null && days[j] <= days[i] && days[j] >= days[i] - 6)
        return s.length ? Math.round((s.reduce((a, b) => a + b, 0) / s.length) * 10) / 10 : null
      })
    },
    options() {
      const cats = this.daily.map(d => {
        const [, m, day] = String(d.date || '').split('-').map(Number)
        if (!m || !day) return ''
        return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1]} ${day}`
      })
      return {
        chart: { type: 'areaspline', height: 260, backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: { categories: cats, lineColor: '#ece7da', tickColor: '#ece7da', labels: { style: { color: '#4a5568' } } },
        yAxis: {
          min: 0, max: 100, tickInterval: 25, title: { text: undefined }, gridLineColor: '#ece7da',
          labels: { format: '{value}%', style: { color: '#4a5568' } }
        },
        tooltip: { valueSuffix: '%', valueDecimals: 0 },
        plotOptions: {
          areaspline: { color: '#2169A8', fillOpacity: 0.1, lineWidth: 2.5, marker: { radius: 3 } }
        },
        series: [{ name: this.name, data: this.points }]
      }
    }
  }
}
</script>

<style scoped>
.psc { background: #fff; border: 1px solid #e6e1d4; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 16px; }
.psc-top { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 16px; }
.psc-titles { display: flex; flex-direction: column; gap: 6px; max-width: 600px; }
.psc-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--brand-ocean-600); }
.psc-titlerow { display: flex; align-items: center; gap: 8px; position: relative; }
.psc h2 { margin: 0; font-size: 24px; font-weight: 700; }
.psc-info { width: 44px; height: 44px; border: none; background: none; cursor: pointer; color: #4a5568; display: inline-flex; align-items: center; justify-content: center; padding: 0; }
.psc-tip { position: absolute; top: 48px; left: 0; z-index: 5; width: min(360px, 90vw); background: var(--brand-ink-950); color: #fff; border-radius: 8px; padding: 14px 16px; font-size: 14px; line-height: 1.5; box-shadow: 0 8px 24px rgba(13, 30, 53, 0.18); }
.psc-toggle { display: flex; border: 1px solid #cfc8b6; border-radius: 8px; overflow: hidden; }
.psc-toggle button { height: 44px; padding: 0 14px; border: none; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; background: #fff; color: var(--brand-ink-950); }
.psc-toggle button.on { background: var(--brand-ink-950); color: #fff; }
.psc-legend { display: flex; gap: 8px; align-items: center; font-size: 14px; }
.psc-line { width: 28px; height: 3px; background: var(--brand-ocean-600); border-radius: 2px; }
.psc-empty { color: #4a5568; padding: 24px 0; }
@media (max-width: 600px) { .psc { padding: 16px; } }
</style>
